/**
 * Subscription Commands
 * Implements the executable commands that other extensions can call
 */

import API from './api';
import { getCurrentUserFromStore } from './auth';
import offlineSubscriptionService from './offlineService';

// Access BodaProContext from window (created in UILayout.js)
const getBodaProContext = () => {
    return window['bodapro-context'] || window.BodaProContext;
};

// Store reference for command callbacks
let storeInstance = null;

/**
 * Pending plan-selection flows, keyed by panel id.
 *
 * showPlansCommand returns a promise that settles when the user finishes with the
 * pricing panel. The resolver can't be handed to the panel through dockview params
 * (functions are dropped when the layout is serialised), so it is parked here and the
 * panel calls completePlanFlow() with its own panel id.
 */
const pendingPlanFlows = new Map();

/**
 * Close a pricing panel and settle the promise showPlansCommand returned for it.
 * Safe to call for a panel opened outside showPlansCommand — it just closes it.
 */
export function completePlanFlow(panelId, result) {
    if (!panelId) return;

    const resolve = pendingPlanFlows.get(panelId);
    pendingPlanFlows.delete(panelId);

    getBodaProContext()?.removePanel(panelId);

    if (resolve) resolve(result);
}

/**
 * Command: subscription.entitlements.check
 * Check subscription status and entitlements
 */
export async function checkEntitlementsCommand(params) {
    try {
        const { extension_key, extension_id, feature_key, quantity = 1 } = params;

        // Require either extension_key or extension_id
        // For offline service, we need extension_key (string)
        const extensionKey = extension_key || (extension_id ? String(extension_id) : null);
        if (!extensionKey) {
            throw new Error('Either extension_key (string) or extension_id (number) is required');
        }

        // Use offline service which handles both online and offline scenarios
        const result = await offlineSubscriptionService.checkEntitlements(
            extensionKey,
            feature_key || '',
            quantity
        );

        // Format response to match expected structure
        if (result.has_subscription !== undefined) {
            return {
                has_subscription: result.has_subscription,
                subscription_status: result.subscription_status || null,
                subscription_id: result.subscription_id || null,
                plan_id: result.plan_id || null,
                allowed: result.allowed !== undefined ? result.allowed : result.has_subscription,
                reason: result.reason || 'ok',
                offline: result.offline || false,
                in_grace_period: result.in_grace_period || false
            };
        }

        // Feature-specific entitlement check response
        return {
            has_subscription: true,
            subscription_status: 'active',
            allowed: result.allowed,
            reason: result.reason,
            remaining: result.remaining,
            reset_at: result.reset_at,
            offline: result.offline || false,
            in_grace_period: result.in_grace_period || false
        };
    } catch (error) {
        console.error('Check entitlements command failed:', error);
        throw error;
    }
}

/**
 * Command: subscription.plans.show
 * Show pricing modal for plan selection
 */
export async function showPlansCommand(params) {
    try {
        let { extension_key, extension_id, currency = 'UGX', highlight_plan } = params;

        if (!extension_key && !extension_id) {
            throw new Error('extension_key or extension_id is required');
        }

        // If only extension_key is provided, look up extension_id
        if (extension_key && !extension_id) {
            try {
                const extension = await API.getExtension(extension_key);
                if (extension && extension.id) {
                    extension_id = extension.id;
                }
            } catch (error) {
                console.warn('Could not look up extension_id from extension_key:', error);
                // Continue without extension_id - backend can handle extension_key
            }
        }

        // Use extension_key for panel title if available
        const displayKey = extension_key || `extension-${extension_id}`;

        // Return a promise that resolves when modal is closed
        return new Promise((resolve) => {
            // const panelId = `pricing-modal-${Date.now()}`;
            const panelId = `pricing-modal-${displayKey}`;

            // Open pricing modal as a panel
            const BodaProContext = getBodaProContext();
            if (!BodaProContext) {
                throw new Error('BodaProContext not available');
            }
            // Parked here rather than passed as a callback prop — see pendingPlanFlows.
            // The panel id is deterministic, so re-invoking this command while the panel
            // is already open supersedes the earlier flow — settle it rather than leaving
            // that caller awaiting forever.
            const supersededFlow = pendingPlanFlows.get(panelId);
            if (supersededFlow) supersededFlow({ action: 'canceled' });
            pendingPlanFlows.set(panelId, resolve);

            BodaProContext.addPanel({
                id: panelId,
                title: 'Choose Your Plan for ' + displayKey,
                componentName: 'subscription-pricing-modal',
                groupId: 'main-panel',
                params: {
                    panelId,
                    extension_key: extension_key,
                    extensionKey: extension_key,
                    extension_id: extension_id,
                    extensionId: extension_id,
                    currency,
                    highlightPlan: highlight_plan,
                    title: params.title // Pass title if provided
                }
            });
        });
    } catch (error) {
        console.error('Show plans command failed:', error);
        throw error;
    }
}

/**
 * Command: subscription.usage.record
 * Record usage for metered features
 */
export async function recordUsageCommand(params) {
    try {
        const { extension_key, extension_id, meter_key, quantity = 1, attributes = {} } = params;

        // Require either extension_key or extension_id
        const extensionIdentifier = extension_id || extension_key;
        if (!extensionIdentifier) {
            throw new Error('Either extension_key (string) or extension_id (number) is required');
        }
        if (!meter_key) {
            throw new Error('meter_key is required');
        }

        // Get current user from store using selector
        const user = getCurrentUserFromStore(storeInstance);

        if (!user) {
            throw new Error('User not authenticated');
        }

        // Generate idempotency key
        const timestamp = Date.now();
        const idempotencyKey = `${user.id}-${extensionIdentifier}-${meter_key}-${timestamp}`;

        // Record usage via API
        // Backend now accepts both extension_id (number) and extension_key (string)
        const usageData = {
            subscriber_id: user.id,
            meter_key,
            quantity,
            timestamp: new Date().toISOString(),
            attributes
        };

        // Add the appropriate field based on what was provided
        if (extension_id) {
            usageData.extension_id = extension_id;
        } else {
            usageData.extension_key = extension_key;
        }

        const data = await API.recordUsage(usageData, idempotencyKey);

        // Check remaining entitlement after recording
        let remaining = null;
        let reset_at = null;

        try {
            const entitlement = await checkEntitlementsCommand({
                extension_key,
                feature_key: meter_key
            });
            remaining = entitlement.entitlement?.remaining || null;
            reset_at = entitlement.entitlement?.reset_at || null;
        } catch (error) {
            console.warn('Could not fetch remaining entitlement:', error);
        }

        return {
            recorded: true,
            usage_id: data.usage_id || data.id,
            remaining,
            reset_at
        };
    } catch (error) {
        console.error('Record usage command failed:', error);
        throw error;
    }
}

/**
 * Command: subscription.access.guard
 * Centralized gating helper so extensions do not have to reimplement paywall logic.
 */
export async function guardAccessCommand(params) {
    const {
        extension_key,
        extension_id,
        feature_key,
        quantity = 1,
        gating_mode = 'warn', // 'block' | 'limited' | 'warn'
        auto_prompt = false,
        prompt_options = {},
        allow_on_error = true
    } = params || {};

    if (!extension_key && !extension_id) {
        throw new Error('subscription.access.guard requires extension_key or extension_id');
    }

    try {
        const entitlement = await checkEntitlementsCommand({
            extension_key,
            extension_id,
            feature_key,
            quantity
        });

        const hasSubscription = entitlement.has_subscription === true;
        const subscriptionStatus = entitlement.subscription_status || null;

        let entitlementAllowed = true;
        let reason = entitlement.reason || 'ok';

        if (!hasSubscription) {
            entitlementAllowed = false;
            reason = entitlement.reason || 'no_active_subscription';
        } else if (feature_key && entitlement.entitlements) {
            entitlementAllowed = entitlement.entitlements.allowed !== false;
            reason = entitlement.entitlements.reason || (entitlementAllowed ? 'ok' : 'limit_reached');
        }

        const result = {
            extension_key: extension_key || null,
            extension_id: extension_id || null,
            feature_key: feature_key || null,
            gating_mode,
            has_subscription: hasSubscription,
            subscription_status: subscriptionStatus,
            allowed: entitlementAllowed,
            reason,
            blocked: false,
            limited: false,
            prompt_triggered: false,
            entitlement
        };

        if (!entitlementAllowed) {
            if (gating_mode === 'block') {
                result.blocked = true;
            } else if (gating_mode === 'limited') {
                result.limited = true;
            }
        }

        if ((result.blocked || (!result.allowed && gating_mode === 'warn')) && auto_prompt) {
            try {
                result.prompt_triggered = true;
                result.prompt_result = await showPlansCommand({
                    extension_key,
                    extension_id,
                    ...prompt_options
                });

                // Refresh entitlement after the modal closes
                if (result.prompt_result?.action === 'subscribed') {
                    const refreshed = await checkEntitlementsCommand({
                        extension_key,
                        extension_id,
                        feature_key,
                        quantity
                    });
                    result.entitlement = refreshed;
                    result.has_subscription = refreshed.has_subscription;
                    result.subscription_status = refreshed.subscription_status;
                    if (feature_key && refreshed.entitlements) {
                        result.allowed = refreshed.entitlements.allowed !== false;
                        result.reason = refreshed.entitlements.reason || 'ok';
                    } else {
                        result.allowed = refreshed.has_subscription;
                        result.reason = refreshed.reason || 'ok';
                    }
                    if (result.allowed) {
                        result.blocked = false;
                        result.limited = false;
                    }
                }
            } catch (promptError) {
                console.error('subscription.access.guard prompt failed:', promptError);
                result.prompt_error = promptError.message;
            }
        }

        return result;
    } catch (error) {
        if (allow_on_error) {
            console.warn('subscription.access.guard failed, falling back to allow:', error);
            return {
                extension_key,
                extension_id,
                feature_key: feature_key || null,
                gating_mode,
                has_subscription: true,
                subscription_status: 'unknown',
                allowed: true,
                reason: 'error_allow',
                blocked: false,
                limited: false,
                prompt_triggered: false,
                error: error.message
            };
        }
        throw error;
    }
}

/**
 * Command: subscription.plans.list
 * Fetch plan details (name, prices, feature limits) for pricing/marketing UI.
 * Unlike plans.show, this returns data only — no UI side effect.
 */
export async function listPlansCommand(params) {
    try {
        const { extension_key, extension_id, status = 'active' } = params || {};

        if (!extension_key && !extension_id) {
            throw new Error('extension_key or extension_id is required');
        }

        const result = await API.getPlans({ extension_key, extension_id, status });
        return { plans: result?.results || (Array.isArray(result) ? result : []) };
    } catch (error) {
        console.error('List plans command failed:', error);
        throw error;
    }
}

/**
 * Register all subscription commands
 */
export function registerSubscriptionCommands(store) {
    // Store reference for use in command callbacks
    storeInstance = store;
    // Set store for offline service
    offlineSubscriptionService.setStore(store);
    const BodaProContext = getBodaProContext();
    
    if (!BodaProContext) {
        console.error('BodaProContext not available');
        return;
    }

    // Register executable commands
    BodaProContext.registerExecutableCommand(
        'subscription.entitlements.check',
        checkEntitlementsCommand
    );

    BodaProContext.registerExecutableCommand(
        'subscription.plans.show',
        showPlansCommand
    );

    BodaProContext.registerExecutableCommand(
        'subscription.usage.record',
        recordUsageCommand
    );

    BodaProContext.registerExecutableCommand(
        'subscription.access.guard',
        guardAccessCommand
    );

    BodaProContext.registerExecutableCommand(
        'subscription.plans.list',
        listPlansCommand
    );

    console.log('Subscription commands registered');
}

